import type {
	RectRoofGeometry,
	RectWallName,
} from "../domain/roof/rectRoofGeometry";
import {
	ENGINE_CLADDING_T_M as CLAD_T,
	ENGINE_CORNER_BOARD_W_M as CORNER_BOARD_W_M,
	ENGINE_BASE_TRIM_H_M,
	ENGINE_BASE_TRIM_TOP_ABOVE_BASE_M,
	ENGINE_CC_M,
	ENGINE_FURRING_D_M as FURR_D,
	ENGINE_FACADE_FURRING_CC_M as FURRING_CC,
	ENGINE_WIND_BARRIER_T_M as WB_T,
} from "./constants";

const RECT_WALLS: readonly RectWallName[] = ["front", "back", "left", "right"];

/**
 * Stud axis positions `generateFramedWall` lays on a wall of this length with
 * no openings: a c/c grid from 0, plus the terminal stud at the far end (a
 * framed panel needs a stud at BOTH ends, so the last bay is short whenever the
 * length isn't a multiple of c/c).
 *
 * Engines that know their openings must pass the real deduped stud axes
 * instead — the renderer draws one ströläkt run per unique axis across ALL stud
 * types (king/jack/cripple/mullion), not per standard stud.
 */
export function defaultStudLinePositions(
	wallLengthM: number,
	ccM: number = ENGINE_CC_M,
): number[] {
	// Step in whole millimetres like `generateFramedWall` does, so the grid lands
	// on 1.8 rather than 1.7999999999999998.
	const ccMm = Math.round(ccM * 1000);
	const wallLengthMm = Math.round(wallLengthM * 1000);
	const positions: number[] = [];
	for (let xMm = 0; xMm < wallLengthMm; xMm += ccMm) {
		positions.push(xMm / 1000);
	}
	positions.push(wallLengthMm / 1000);
	return positions;
}

/** Default (opening-free) stud lines for all four perimeter walls. */
export function defaultStudLinesByWall(
	W: number,
	L: number,
): Record<RectWallName, number[]> {
	return {
		front: defaultStudLinePositions(W),
		back: defaultStudLinePositions(W),
		left: defaultStudLinePositions(L),
		right: defaultStudLinePositions(L),
	};
}

export interface VentilatedFacadeQuantities {
	/**
	 * Standing ströläkt: one run per exterior stud line, each as tall as the
	 * wall is AT THAT STUD (roof profile), not the flat wall height.
	 */
	lmStroToLakt: number;
	/** Lying spikläkt total, summed per wall to each wall's max roof height. */
	lmHorizontalFurring: number;
	/** Wind barrier board/membrane behind the air gap — one wall area's worth. */
	windBarrierArea: number;
	cornerTrimLm: number;
	lmBaseTrim: number;
	/** Front/back nederkantsavslut board length (matches the rendered board). */
	baseTrimCutFrontBackM: number;
	/** Left/right nederkantsavslut board length (matches the rendered board). */
	baseTrimCutSideM: number;
}

/**
 * BOM quantities for the ventilated facade layer stack that
 * `buildExteriorFacade` renders in `ventilated` mode. One definition shared
 * by the square and two-room engines so the purchase list always mirrors the
 * 3D build-up (vindskydd → stående ströläkt → liggande spikläkt → panel).
 *
 * Takes the engines' `roofGeometry` so the roof-aware layers match the
 * renderer exactly: the standing ströläkt runs are as tall as the wall is at
 * each stud line, the lying spikläkt rows run up to each wall's max height
 * (gable ridge / mono high eave), and the knutbrädor are sized to the four
 * corner roof heights.
 */
export function calculateVentilatedFacadeQuantities(input: {
	W: number;
	L: number;
	/**
	 * Exterior stud axis positions per wall, in metres from the wall's local
	 * origin — the same list `renderStudAlignedStrips` receives. Use
	 * `defaultStudLinesByWall` when the caller has no opening data.
	 */
	studLinesByWall: Record<RectWallName, number[]>;
	wallArea: number;
	roofGeometry: RectRoofGeometry;
}): VentilatedFacadeQuantities {
	const { W, L, studLinesByWall, wallArea, roofGeometry } = input;
	// Depth the facade adds outside the studs: wind barrier + korsläkt + panel.
	const facadeBuildout = WB_T + 2 * FURR_D + CLAD_T;

	// Standing ströläkt: renderStudAlignedStrips draws one run per UNIQUE stud
	// axis and gives it `getWallHeightAt(wall, x)` — the real roof profile — so
	// runs on a gable end wall reach into the triangle and runs on a mono side
	// wall climb toward the high eave. A flat `totalStuds * H` under-bought
	// every roof but a perfectly flat one (~5 % at 22°, worse when steeper).
	// Openings are NOT subtracted here although the renderer splits runs around
	// them: over-buying is the safe direction and matches the other rows.
	const lmStroToLakt = Math.ceil(
		RECT_WALLS.reduce((sum, wall) => {
			const positions = [...new Set(studLinesByWall[wall] ?? [])];
			return (
				sum +
				positions.reduce(
					(wallSum, localX) =>
						wallSum + roofGeometry.getWallHeightAt(wall, localX),
					0,
				)
			);
		}, 0),
	);

	// Lying spikläkt: renderHorizontalFurringStrips generates rows up each
	// wall to its max height (gable ridge, mono high eave), so compute rows
	// per wall from that height instead of the flat wall height. This slightly
	// over-buys on clipped gable triangles (rows are counted over the full
	// rectangle, the renderer clips them to the roof profile) — a safe
	// direction, consistent with the repo's other waste margins.
	const wallSpans: Array<{
		wall: "front" | "back" | "left" | "right";
		length: number;
	}> = [
		{ wall: "front", length: W },
		{ wall: "back", length: W },
		{ wall: "left", length: L },
		{ wall: "right", length: L },
	];
	const lmHorizontalFurring = Math.ceil(
		wallSpans.reduce((sum, { wall, length }) => {
			const rows =
				Math.ceil(roofGeometry.getMaxWallHeight(wall) / FURRING_CC) + 1;
			return sum + length * rows;
		}, 0),
	);

	const windBarrierArea = Math.ceil(wallArea);

	// Knutbrädor: addInterlockingCornerBoards sizes each of the eight boards
	// (two per corner) to the corner's roof height plus a bottom extension that
	// runs the board down to the base trim's drip edge.
	const bottomExt = ENGINE_BASE_TRIM_H_M - ENGINE_BASE_TRIM_TOP_ABOVE_BASE_M;
	const corners: Array<[number, number]> = [
		[-1, -1],
		[-1, 1],
		[1, -1],
		[1, 1],
	];
	const cornerTrimLm = Math.ceil(
		corners.reduce((sum, [sx, sz]) => {
			const cornerHeight = roofGeometry.getRoofHeightAt(
				(sx * W) / 2,
				(sz * L) / 2,
			);
			return sum + 2 * (cornerHeight + bottomExt);
		}, 0),
	);

	// Nederkantsavslut: the rendered front/back board spans
	// (cladOuterFaceX - CORNER_BOARD_W_M) * 2, butting into the knutbrädor at
	// each end. cladOuterFaceX = W/2 + facadeBuildout (cladOffset + CLAD_T/2).
	// NB: walls with a deck attached render without base trim (see
	// squareBaseBuilder/facade.ts baseTrimSuppressedWalls), but the BOM still
	// counts all four sides — the small over-buy is an acceptable waste margin.
	const baseTrimCutFrontBackM = (W / 2 + facadeBuildout - CORNER_BOARD_W_M) * 2;
	const baseTrimCutSideM = (L / 2 + facadeBuildout - CORNER_BOARD_W_M) * 2;
	const lmBaseTrim = Math.ceil(
		2 * (W + facadeBuildout * 2) + 2 * (L + facadeBuildout * 2),
	);

	return {
		lmStroToLakt,
		lmHorizontalFurring,
		windBarrierArea,
		cornerTrimLm,
		lmBaseTrim,
		baseTrimCutFrontBackM,
		baseTrimCutSideM,
	};
}
